/**
 * Storage utility for client filters
 *
 * This provides tab-specific storage for client filters using sessionStorage,
 * which is isolated to the current tab/window. This allows different tabs
 * to have different filter settings.
 */

import { clientFiltersStorageKey } from "@src/constants/localStorageKeys";
import { ClientSearchFiltersCheckboxLabel } from "@src/multiCustodian/components/Advisor/Clients/types";
import { CheckboxListConfig } from "@src/sharedComponents/ScrollableCheckboxList/ScrollableCheckboxList";

// Define key for tab-specific storage
const tabKey = `${clientFiltersStorageKey}.tab`;

// Client filter parameters interface
/* eslint-disable import/no-unused-modules */
export interface ClientFilterParams {
  searchFilters?: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>;

  // The entire URL query string to restore (includes clientQuery)
  url?: string;
}

/**
 * Saves client filters to tab-specific storage (sessionStorage)
 */
export function saveFiltersToTab(filters: ClientFilterParams): void {
  try {
    // Only save URL in storage if it's non-empty
    const filtersToSave = {
      ...filters,
    };

    // Only save the URL if it has actual query parameters
    if (filters.url && filters.url !== "") {
      filtersToSave.url = filters.url;
    } else {
      // Don't save empty URLs, and remove any existing URL from storage
      delete filtersToSave.url;
    }

    sessionStorage.setItem(tabKey, JSON.stringify(filtersToSave));
  } catch (error) {
    console.error("Error saving client filters to session storage:", error);
  }
}

/**
 * Gets client filters from tab-specific storage (sessionStorage)
 */
export function getFiltersFromTab(
  defaultValue: ClientFilterParams
): ClientFilterParams {
  try {
    const savedFilters = sessionStorage.getItem(tabKey);
    console.log("Raw saved filters from sessionStorage:", savedFilters);

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters) as ClientFilterParams;
      // Basic validation
      if (parsedFilters && typeof parsedFilters === "object") {
        // Ensure url is actually a string
        if (parsedFilters.url) {
          console.log("Found URL in session storage:", parsedFilters.url);
        }

        // Ensure new filters are merged with saved values
        parsedFilters.searchFilters = {
          ...defaultValue.searchFilters,
          ...parsedFilters.searchFilters,
        } as CheckboxListConfig<ClientSearchFiltersCheckboxLabel>;

        return parsedFilters;
      }
    }
  } catch (error) {
    console.error("Error loading client filters from session storage:", error);
  }

  return defaultValue;
}

/**
 * Resets client filters in tab-specific storage (sessionStorage)
 */
export function resetTabFilters(defaultParams: ClientFilterParams): boolean {
  try {
    // Create deep copy to avoid reference issues
    const params = JSON.parse(JSON.stringify(defaultParams));
    sessionStorage.setItem(tabKey, JSON.stringify(params));
    return true;
  } catch (error) {
    console.error("Error resetting tab client filters:", error);
    return false;
  }
}

/**
 * Utility to clean up filter storage when no longer needed
 */
export function cleanupClientFilters(): void {
  try {
    sessionStorage.removeItem(tabKey);
  } catch (error) {
    console.error("Error cleaning up client filters:", error);
  }
}
